/**
 * [화면 편집] — 쉬운 판(2026-10-30). HTML 을 화면 그대로 띄워 놓고, 보이는 것을 눌러 고친다.
 *
 * 쓰는 사람은 코드도 디자인 도구도 모른다. 그래서 디자인 도구의 개념(레이어 나무·묶음 깊이·도구 바꾸기·X·Y·W·H·단축키)을
 * 앞에 내지 않는다.
 * - 누르면 **보이는 그대로 하나**가 골린다 — 글이면 그 글 덩이, 그림이면 그 그림, 아니면 그 상자(`dom.ts` 의 `pickTarget`).
 *   더 큰 묶음이 필요하면 오른쪽 칸의 [감싼 상자 고르기].
 * - 끌면 옮겨지고, 테두리의 동그라미를 끌면 크기가 바뀐다. 다른 요소의 변·가운데에 가까워지면 붙는다(빨간 맞춤선).
 * - 고른 글을 한 번 더 누르면(두 번 눌러도) 그 자리에서 글을 고친다.
 * - 고른 것 바로 위에 [글 고치기 · 복제 · 지우기], 오른쪽 칸에는 그것에 맞는 것만(`EditPanel.tsx`).
 * - 넣기(글·그림·상자)·되돌리기·저장은 위 막대에 글자로. 빈 곳을 끌면 화면이 옮겨지고, 아래에 확대·축소.
 * 그 전의 피그마식 판(레이어·디자인 칸·도구 막대)은 `1e9fd7e`(태그 `html-editor/figma-v1`)에 있다.
 *
 * 고친 결과는 **그 요소의 인라인 스타일**로만 남긴다(`model.ts`). 문서는 스크립트 없이 띄운다(`sandbox="allow-same-origin"`)
 * — 남이 짠 스크립트가 콘솔에서 돌지 않고, 부모가 문서를 고칠 수 있다. 그 문서 안의 리스너는 WebKit 이 부르지 않으므로
 * 마우스는 캔버스가 받고, 무엇을 눌렀는지는 문서에 `elementsFromPoint` 로 묻는다(`dom.ts`). 고른 테두리·손잡이·맞춤선은
 * 캔버스(화면 좌표)에 그린다 — 문서에 편집용 요소를 넣지 않아 저장한 글에 남지 않는다.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, Clapperboard, Copy, ImageUp, Maximize, Minus, Pencil, Plus, Redo2, Trash2, Undo2, X } from 'lucide-react';
import { VIDEO_ATTR } from '@signageone/core';
import { useGuardedClose } from '../components/closeGuard.js';
import {
  alignDeltas,
  clampZoom,
  EditHistory,
  elementLabel,
  expandInsetShorthand,
  fitView,
  fitWithin,
  HANDLE_CURSOR,
  HANDLES,
  handlePoint,
  moveOrigin,
  movedStyle,
  parseCssColor,
  resizeBox,
  serializeHtml,
  sizedStyle,
  snapMove,
  snapResize,
  stepZoom,
  topColors,
  unionBox,
  zoomAround,
  type AlignMode,
  type Box,
  type Guide,
  type Handle,
  type MoveOrigin,
  type Pan,
} from './model.js';
import {
  atPath,
  backdropOf,
  boxOf,
  deepestAt,
  isDday,
  isImageFrame,
  isTextLayer,
  isVideo,
  layerChildren,
  LOCK_ATTR,
  parentTarget,
  pathOf,
  pickTarget,
  placeAbsolute,
  rootFrame,
  setInline,
  SVG_NS,
  zIndexOf,
} from './dom.js';
import { EditPanel, type DesignSize, type EditActions } from './EditPanel.js';
import { InsertMenu } from './InsertMenu.js';
import {
  buildComponent,
  clearImageFrame,
  createVideo,
  dropVideoSources,
  ensureId,
  fieldKind,
  fillImageFrame,
  frameImage,
  setFieldValue,
  setFrameFit,
  readDday,
  refreshDdays,
  setSelectOptions,
  setVideoAsset,
  setVideoFit,
  setVideoSound,
  syncRuntimeScripts,
  writeDday,
  type ComponentId,
  type DdaySettings,
  type ImageFit,
} from './components.js';

export type { DesignSize } from './EditPanel.js';

export interface HtmlVisualEditorProps {
  title: string;
  html: string;
  design: DesignSize;
  /** 늘고 주는 HTML 처럼 편집 크기를 고를 수 있을 때 — 고른 크기가 저장 뒤의 기준 크기가 된다. */
  sizeChoices?: ReadonlyArray<{ label: string } & DesignSize>;
  /** 캔버스 위에 띄울 알림(예: 템플릿 사본이라는 것). */
  note?: ReactNode;
  /** 저장 단추 옆에 둘 것(예: 플레이리스트에서 바꿔 넣기). */
  saveExtras?: ReactNode;
  saveLabel?: string;
  /** 고친 것이 없어도 저장할 수 있다 — 등록한 템플릿의 사본은 새 이름만으로도 새 콘텐츠다. */
  savePristine?: boolean;
  /** 빈 화면에서 시작할 때 — [넣기] 메뉴를 펼친 채로 연다. */
  startWithInsert?: boolean;
  /**
   * 영상을 콘텐츠로 올린다(2026-10-30) — 문서에는 그 자산 id 만 적는다(`components.ts`). 없으면 [넣기] ▸ [영상] 을 감춘다.
   */
  uploadVideo?: (file: File) => Promise<{ id: string }>;
  /** 문서의 영상을 편집 화면에서 보일 주소(콘솔 미리보기) — 저장에는 빠진다. */
  videoSource?: (id: string) => Promise<string | null>;
  onCancel: () => void;
  onSave: (html: string, design: DesignSize) => Promise<void>;
}

/** 고를 수 있는 그림 파일의 상한 — 큰 사진은 넣기 전에 줄인다(`fitWithin`). */
const IMAGE_FILE_MAX_BYTES = 20 * 1024 * 1024;
/** 문서에 넣는 그림 하나의 상한(줄인 뒤) — 문서 안에 `data:` 로 품으므로 문서가 그만큼 커진다(올린 HTML 상한 10MB). */
const IMAGE_MAX_BYTES = 3 * 1024 * 1024;
/** 넣는 그림의 긴 변 — 화면(1920)보다 조금 크게. */
const IMAGE_MAX_SIDE = 2048;
/** 맞춤선에 붙는 거리 — 화면의 픽셀(확대와 상관없이 손에 같은 느낌). */
const SNAP_PX = 6;
const BLUE = '#2563eb';
const RED = '#f24822';

interface Point {
  x: number;
  y: number;
}

type Drag =
  | { kind: 'text'; anchor: Range }
  | { kind: 'pan'; x: number; y: number; pan: Pan }
  | {
      kind: 'move';
      start: Point;
      moved: boolean;
      items: Array<{ element: HTMLElement; origin: MoveOrigin }>;
      base: Box | null;
      targets: Box[];
      /** 끌지 않고 뗐을 때 — 고른 글이면 그 자리에서 고치기, 고른 상자 안의 것이면 그것을 고른다. */
      click: { edit: HTMLElement } | { pick: HTMLElement } | null;
    }
  | {
      kind: 'resize';
      element: HTMLElement;
      handle: Handle;
      start: Point;
      box: Box;
      origin: MoveOrigin;
      extra: { width: number; height: number };
      keepRatio: boolean;
      moved: boolean;
      targets: Box[];
    };

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = IS_MAC ? '⌘' : 'Ctrl';

export function HtmlVisualEditor({
  title,
  html,
  design: initialDesign,
  sizeChoices,
  note,
  saveExtras,
  saveLabel = '저장',
  savePristine = false,
  startWithInsert = false,
  uploadVideo,
  videoSource,
  onCancel,
  onSave,
}: HtmlVisualEditorProps): ReactNode {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  /** 그림 파일을 고른 뒤 할 일 — 새 그림 넣기 · 고른 그림 바꾸기 · 고른 그림 영역에 넣기. */
  const fileModeRef = useRef<'add' | 'replace' | 'frame'>('add');
  const videoFileRef = useRef<HTMLInputElement>(null);
  /** 영상 파일을 고른 뒤 할 일 — 새 영상 넣기 · 고른 영상 바꾸기. */
  const videoModeRef = useRef<'add' | 'replace'>('add');
  /** 영상을 편집 화면에서 보일 주소 — 자산 id 마다(방금 올린 것은 내 컴퓨터의 파일). 묻는 중인 id 도 적는다. */
  const videoSourcesRef = useRef(new Map<string, string | null>());
  const [videoBusy, setVideoBusy] = useState(false);
  const historyRef = useRef<EditHistory | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const selectionRef = useRef<HTMLElement[]>([]);
  const editingRef = useRef<HTMLElement | null>(null);
  const clipboardRef = useRef<Array<{ node: HTMLElement; box: Box; parentPath: number[] | null }>>([]);
  const spaceRef = useRef(false);
  const commitTimer = useRef<number | null>(null);
  /** 잇달아 넣은 요소가 한 자리에 겹치지 않게 조금씩 비킨다 — 고르거나 옮기면 다시 가운데부터. */
  const insertStepRef = useRef(0);

  const [design, setDesign] = useState<DesignSize>(initialDesign);
  const [view, setView] = useState<{ zoom: number; pan: Pan; fit: boolean }>({
    zoom: 0.4,
    pan: { x: 0, y: 0 },
    fit: true,
  });
  const viewRef = useRef(view);
  viewRef.current = view;
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [ready, setReady] = useState(false);
  const [, setVersion] = useState(0);
  const [structure, setStructure] = useState(0);
  const [, setSelectionVersion] = useState(0);
  const [hovered, setHovered] = useState<HTMLElement | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [resizing, setResizing] = useState(false);
  const [editingText, setEditingText] = useState(false);
  const [panning, setPanning] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [menu, setMenu] = useState<Point | null>(null);
  const [insertOpen, setInsertOpen] = useState(startWithInsert);
  const [showNote, setShowNote] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bump = (): void => setVersion((value) => value + 1);
  const bumpStructure = (): void => setStructure((value) => value + 1);

  const doc = (): Document | null => frameRef.current?.contentDocument ?? null;
  const win = (): Window | null => frameRef.current?.contentWindow ?? null;
  /** 오른쪽 칸에 넘길 창 — 늘 같은 함수(창을 props 로 넘기지 않는다, `EditPanel` 참고). */
  const getView = useCallback((): Window | null => (ready ? (frameRef.current?.contentWindow ?? null) : null), [ready]);
  const frameBox: Box = { x: 0, y: 0, width: design.width, height: design.height };
  const frameRoot = (): HTMLElement | null => {
    const document_ = doc();
    return document_?.body ? rootFrame(document_, frameBox) : null;
  };
  /** [원래대로] 가 볼 처음 문서 — 자리(`pathOf`)로 같은 요소를 찾는다. 되돌리기로 문서를 새로 써도 맞는다. */
  const original = useMemo(() => new DOMParser().parseFromString(html, 'text/html'), [html]);

  const serialize = useCallback((): string => {
    const document_ = doc();
    if (!document_) return '';
    const root = document_.documentElement.cloneNode(true) as HTMLElement;
    for (const element of Array.from(root.querySelectorAll('[contenteditable]')))
      element.removeAttribute('contenteditable');
    for (const element of Array.from(root.querySelectorAll('[style*="inset"]'))) {
      element.setAttribute('style', expandInsetShorthand(element.getAttribute('style') ?? ''));
    }
    // 편집 화면에만 단 영상 주소는 뗀다. 입력 칸에 정한 값을 화면에서 다시 넣고, D-day 를 다시 그리고, 영상을 받아 트는
    // 스크립트(`components.ts`).
    dropVideoSources(root);
    syncRuntimeScripts(root);
    const type = document_.doctype;
    return serializeHtml(
      type ? { name: type.name, publicId: type.publicId, systemId: type.systemId } : null,
      root.outerHTML,
    );
  }, []);

  /** 문서를 통째로 쓴다(처음 열 때·되돌리기). 스크립트는 돌지 않는다 — 샌드박스가 막는다. */
  const writeDocument = useCallback((text: string): void => {
    const document_ = doc();
    if (!document_) return;
    document_.open();
    document_.write(text);
    document_.close();
    selectionRef.current = [];
    editingRef.current = null;
    setHovered(null);
    setEditingText(false);
  }, []);

  /**
   * 문서의 영상에 편집 화면용 주소를 단다 — 문서에는 주소가 없다(`components.ts`). 처음 보는 id 는 콘솔에 물어 둔다.
   * 문서를 다시 쓸 때마다(열 때·되돌리기) 부른다. 저장에는 빠진다(`dropVideoSources`).
   */
  const showVideos = useCallback((): void => {
    const body = doc()?.body;
    if (!body) return;
    for (const video of Array.from(body.querySelectorAll<HTMLVideoElement>(`video[${VIDEO_ATTR}]`))) {
      const id = video.getAttribute(VIDEO_ATTR) ?? '';
      const sources = videoSourcesRef.current;
      if (sources.has(id)) {
        const source = sources.get(id);
        if (source && video.getAttribute('src') !== source) {
          video.setAttribute('src', source);
          // 주소를 나중에 단 영상은 autoplay 를 놓치는 브라우저가 있다(WebKit).
          void video.play?.()?.catch(() => undefined);
        }
        continue;
      }
      if (!videoSource) continue;
      sources.set(id, null);
      void videoSource(id).then(
        (source) => {
          sources.set(id, source);
          if (source) showVideos();
        },
        () => undefined,
      );
    }
  }, [videoSource]);

  // 처음 열 때 — 문서를 쓰고, 그 결과를 되돌리기의 출발점으로 둔다.
  useEffect(() => {
    writeDocument(html);
    showVideos();
    // D-day 는 오늘 기준으로 — 며칠 전에 저장한 글이어도. 되돌리기의 출발점보다 먼저 해서 연 것만으로 "고침" 이 되지 않게.
    const body = doc()?.body;
    if (body) refreshDdays(body);
    historyRef.current = new EditHistory(serialize());
    setReady(true);
    bumpStructure();
  }, [html, serialize, writeDocument, showVideos]);

  // 화면에 맞추기 — 맞춤 상태면 칸 크기가 바뀔 때마다 다시 맞춘다.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const refit = (force = false): void => {
      const rect = canvas.getBoundingClientRect();
      setCanvasSize({ width: rect.width, height: rect.height });
      if (!force && !viewRef.current.fit) return;
      setView({ ...fitView(rect, { x: 0, y: 0, width: design.width, height: design.height }, 40), fit: true });
    };
    refit(true);
    const observer = new ResizeObserver(() => refit());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [design]);

  // ── 남기기 — 되돌리기 한 칸. 끄는 동안 바뀐 것은 놓을 때, 밀대·색 고르기처럼 잇달아 바뀌는 것은 잠깐 뒤에 한 번.

  const commit = useCallback((): void => {
    if (commitTimer.current !== null) {
      window.clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
    const history = historyRef.current;
    if (history?.push(serialize())) bumpStructure();
    bump();
  }, [serialize]);

  const commitSoon = (): void => {
    bump();
    if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
    commitTimer.current = window.setTimeout(() => {
      commitTimer.current = null;
      commit();
    }, 400);
  };

  const flush = (): void => {
    if (commitTimer.current !== null) commit();
  };

  // ── 고르기

  const select = (list: readonly HTMLElement[]): void => {
    const unique = [...new Set(list)].filter((element) => element.isConnected);
    selectionRef.current = unique.filter((element) => !unique.some((other) => other !== element && other.contains(element)));
    setSelectionVersion((value) => value + 1);
    bump();
  };

  /** 고르지 않는 바탕 — 문서의 틀과 그 바깥. */
  const backdrop = (): Set<Element> => {
    const root = frameRoot();
    return root ? backdropOf(root) : new Set();
  };

  /** 누른 자리에서 고를 것 — 보이는 그대로 하나(`pickTarget`). */
  const pickAt = (point: Point): HTMLElement | null => {
    const document_ = doc();
    const view = win();
    if (!document_ || !view) return null;
    return pickTarget(deepestAt(document_, point), backdrop(), view);
  };

  const { zoom, pan } = view;
  const toScreen = (box: Box): Box => ({
    x: box.x * zoom + pan.x,
    y: box.y * zoom + pan.y,
    width: box.width * zoom,
    height: box.height * zoom,
  });
  const pointOf = (event: { clientX: number; clientY: number }): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: (event.clientX - rect.left - pan.x) / zoom, y: (event.clientY - rect.top - pan.y) / zoom };
  };
  const inside = (box: Box | null, point: Point): boolean =>
    !!box && point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height;
  const computed = (element: Element): CSSStyleDeclaration => win()!.getComputedStyle(element);

  // ── 옮기기·크기 — 인라인 스타일로만(`model.ts`).

  /**
   * 절대 자리의 요소는 지금 크기로 굳히고 인라인의 오른쪽·아래를 지운다 — 왼쪽·위를 바꿀 때 늘어나지 않게. 너비·높이가 정해져
   * 있으면 오른쪽·아래는 어차피 무시된다. `right: auto` 로 덮지 않는 까닭: 네 변이 다 인라인에 있으면 브라우저가 `inset:` 한
   * 줄로 합쳐 적는데, 안드로이드 5.1 웹뷰(Chromium 39)는 그 줄을 모른다.
   */
  const prepareMove = (element: HTMLElement): MoveOrigin => {
    const style = computed(element);
    const origin = moveOrigin(style);
    if (origin.position !== 'relative') {
      // 올림 — 소수점을 버려 글줄이 한 칸 모자라 줄바꿈되지 않게.
      setInline(element, {
        width: `${Math.ceil(Number.parseFloat(style.width) || 0)}px`,
        height: `${Math.ceil(Number.parseFloat(style.height) || 0)}px`,
        right: null,
        bottom: null,
      });
    }
    return origin;
  };

  const applyMove = (element: HTMLElement, origin: MoveOrigin, dx: number, dy: number): void => {
    setInline(element, movedStyle(origin, dx, dy));
  };

  const moveBy = (elements: readonly HTMLElement[], deltas: ReadonlyArray<{ dx: number; dy: number }>): void => {
    elements.forEach((element, index) => {
      const delta = deltas[index]!;
      if (delta.dx === 0 && delta.dy === 0) return;
      applyMove(element, prepareMove(element), delta.dx, delta.dy);
    });
  };

  const prepareResize = (element: HTMLElement): { origin: MoveOrigin; extra: { width: number; height: number } } => {
    let style = computed(element);
    // 글줄 속 요소(span 등)는 크기를 받지 않는다 — 글줄 상자로 바꾼다. 그림은 그대로도 크기를 받는다.
    const changes: Record<string, string> = { 'max-width': 'none', 'max-height': 'none' };
    if (style.display === 'inline' && element.tagName.toLowerCase() !== 'img') changes.display = 'inline-block';
    const parent = element.parentElement;
    if (parent && /flex/.test(computed(parent).display)) changes.flex = '0 0 auto';
    setInline(element, changes);
    style = computed(element);
    const box = boxOf(element);
    const origin = prepareMove(element);
    return {
      origin,
      extra: {
        width: box.width - (Number.parseFloat(style.width) || box.width),
        height: box.height - (Number.parseFloat(style.height) || box.height),
      },
    };
  };

  const applyResize = (drag: Extract<Drag, { kind: 'resize' }>, box: Box): void => {
    setInline(drag.element, sizedStyle(box, drag.extra));
    if (box.x !== drag.box.x || box.y !== drag.box.y)
      applyMove(drag.element, drag.origin, box.x - drag.box.x, box.y - drag.box.y);
  };

  /** 붙을 곳 — 화면, 감싼 상자, 같은 층의 다른 요소. */
  const snapTargets = (moving: readonly HTMLElement[]): Box[] => {
    const document_ = doc();
    const out: Box[] = [frameBox];
    if (!document_) return out;
    const parents = new Set(
      moving.map((element) => element.parentElement).filter((parent): parent is HTMLElement => !!parent),
    );
    for (const parent of parents) {
      if (parent !== document_.body && parent !== document_.documentElement) out.push(boxOf(parent));
      for (const sibling of layerChildren(parent)) {
        if (moving.includes(sibling)) continue;
        const box = boxOf(sibling);
        if (box.width > 0 && box.height > 0) out.push(box);
      }
    }
    return out;
  };

  /** 제자리에 복제 — 흐름에서 떼어 조금 비킨 자리에 둔다. 원본 뒤에 넣어 위에 그려진다. */
  const duplicateOf = (element: HTMLElement, offset: number): HTMLElement => {
    const box = boxOf(element);
    const clone = element.cloneNode(true) as HTMLElement;
    clone.removeAttribute(LOCK_ATTR);
    element.after(clone);
    placeAbsolute(clone, { ...box, x: box.x + offset, y: box.y + offset });
    return clone;
  };

  /** 새 요소가 같은 층의 맨 위에 보이게. */
  const raiseNew = (element: HTMLElement): void => {
    const parent = element.parentElement;
    const view = win();
    if (!parent || !view) return;
    const top = Math.max(
      0,
      ...layerChildren(parent)
        .filter((sibling) => sibling !== element)
        .map((sibling) => zIndexOf(sibling, view)),
    );
    if (top > 0) setInline(element, { 'z-index': String(top + 1) });
  };

  // ── 글자 고치기

  const finishText = useCallback((): void => {
    const element = editingRef.current;
    if (!element) return;
    element.removeAttribute('contenteditable');
    editingRef.current = null;
    setEditingText(false);
    dialogRef.current?.focus({ preventScroll: true });
    commit();
  }, [commit]);

  const startText = (element: HTMLElement | null, selectAll = false): void => {
    const document_ = doc();
    if (!element || !document_) return;
    if (editingRef.current && editingRef.current !== element) finishText();
    element.setAttribute('contenteditable', 'true');
    editingRef.current = element;
    selectionRef.current = [element];
    setSelectionVersion((value) => value + 1);
    setEditingText(true);
    setHovered(null);
    element.focus({ preventScroll: true });
    const range = document_.createRange();
    range.selectNodeContents(element);
    if (!selectAll) range.collapse(false);
    const selection = document_.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  /** 판 위의 자리에 해당하는 글자 사이. */
  const caretAt = (point: Point): Range | null => {
    const document_ = doc() as
      | (Document & { caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null })
      | null;
    if (!document_) return null;
    if (typeof document_.caretRangeFromPoint === 'function') return document_.caretRangeFromPoint(point.x, point.y);
    const position = document_.caretPositionFromPoint?.(point.x, point.y);
    if (!position) return null;
    const range = document_.createRange();
    range.setStart(position.offsetNode, position.offset);
    return range;
  };

  const setCaret = (anchor: Range, focus: Range): void => {
    doc()
      ?.getSelection()
      ?.setBaseAndExtent(anchor.startContainer, anchor.startOffset, focus.startContainer, focus.startOffset);
  };

  /** 누른 자리에 커서를 두고 글 고치기를 시작한다. */
  const startTextAt = (element: HTMLElement, point: Point): void => {
    startText(element);
    const range = caretAt(point);
    if (range && element.contains(range.startContainer)) setCaret(range, range);
  };

  const selectWordAt = (point: Point): void => {
    const editing = editingRef.current;
    const range = editing ? caretAt(point) : null;
    if (!editing || !range || !editing.contains(range.startContainer)) return;
    setCaret(range, range);
    const selection = doc()?.getSelection() as
      (Selection & { modify?: (alter: string, direction: string, granularity: string) => void }) | null;
    selection?.modify?.('move', 'backward', 'word');
    selection?.modify?.('extend', 'forward', 'word');
  };

  // 글을 고치는 동안 — 글이 늘고 주면 고른 테두리가 따라가게 그림마다 다시 잰다(문서 안의 리스너는 불리지 않는다).
  useEffect(() => {
    if (!editingText) return;
    let frame = 0;
    let last = '';
    const follow = (): void => {
      const element = editingRef.current;
      if (element) {
        const box = boxOf(element);
        const key = `${box.x},${box.y},${box.width},${box.height}`;
        if (key !== last) {
          last = key;
          bump();
        }
      }
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
  }, [editingText]);

  // ── 고치는 동작

  const restore = (state: string): void => {
    const body = doc()?.body;
    const paths = body
      ? selectionRef.current.map((element) => pathOf(body, element)).filter((path): path is number[] => !!path)
      : [];
    writeDocument(state);
    showVideos();
    const next = doc()?.body;
    select(next ? paths.map((path) => atPath(next, path)).filter((element): element is HTMLElement => !!element) : []);
    bumpStructure();
  };
  const undo = (): void => {
    if (editingRef.current) finishText();
    flush();
    const state = historyRef.current?.undo();
    if (state !== null && state !== undefined) restore(state);
  };
  const redo = (): void => {
    if (editingRef.current) finishText();
    flush();
    const state = historyRef.current?.redo();
    if (state !== null && state !== undefined) restore(state);
  };

  const originalOf = (element: HTMLElement): Element | null => {
    const body = doc()?.body;
    const path = body ? pathOf(body, element) : null;
    const found = path ? atPath(original.body, path) : null;
    return found && found.tagName === element.tagName ? found : null;
  };
  const canReset = selectionRef.current.some((element) => {
    const found = originalOf(element);
    return !!found && (found.getAttribute('style') ?? '') !== (element.getAttribute('style') ?? '');
  });
  const resetSelection = (): void => {
    for (const element of selectionRef.current) {
      const found = originalOf(element);
      if (!found) continue;
      const style = found.getAttribute('style');
      if (style === null) element.removeAttribute('style');
      else element.setAttribute('style', style);
    }
    commit();
  };

  /** [처음 모습으로 모두 되돌리기] — 처음 연 문서를 다시 쓴다. 되돌리기 한 칸으로 남아 다시 하기로 돌아올 수 있다. */
  const resetAll = (): void => {
    if (editingRef.current) finishText();
    flush();
    writeDocument(html);
    select([]);
    commit();
    bumpStructure();
  };

  const removeSelection = (): void => {
    if (selectionRef.current.length === 0) return;
    if (editingRef.current) finishText();
    for (const element of selectionRef.current) element.remove();
    select([]);
    commit();
  };

  const duplicateSelection = (): void => {
    if (selectionRef.current.length === 0) return;
    if (editingRef.current) finishText();
    select(selectionRef.current.map((element) => duplicateOf(element, 24)));
    commit();
  };

  const copySelection = (): void => {
    const body = doc()?.body;
    if (!body) return;
    clipboardRef.current = selectionRef.current.map((element) => ({
      node: element.cloneNode(true) as HTMLElement,
      box: boxOf(element),
      parentPath: element.parentElement ? pathOf(body, element.parentElement) : null,
    }));
  };

  const paste = (): void => {
    const body = doc()?.body;
    if (!body || clipboardRef.current.length === 0) return;
    const pasted: HTMLElement[] = [];
    for (const item of clipboardRef.current) {
      const parent =
        (item.parentPath && item.parentPath.length > 0 ? atPath(body, item.parentPath) : null) ?? frameRoot() ?? body;
      const clone = item.node.cloneNode(true) as HTMLElement;
      clone.removeAttribute(LOCK_ATTR);
      parent.append(clone);
      // 붙일 때마다 조금씩 비켜 겹친 것이 보이게.
      item.box = { ...item.box, x: item.box.x + 24, y: item.box.y + 24 };
      placeAbsolute(clone, item.box);
      raiseNew(clone);
      pasted.push(clone);
    }
    select(pasted);
    commit();
  };

  /** 앞으로·뒤로 — 흐름 밖(절대 자리)이면 문서 차례를 바꾸고, 흐름 속이면 겹침 차례(z-index)로. */
  const reorder = (direction: 1 | -1): void => {
    const view = win();
    if (!view) return;
    for (const element of selectionRef.current) {
      const position = computed(element).position;
      if (position === 'absolute' || position === 'fixed') {
        const siblings = element.parentElement ? layerChildren(element.parentElement) : [];
        const index = siblings.indexOf(element);
        const neighbour = siblings[index + direction];
        if (neighbour) {
          if (direction > 0) neighbour.after(element);
          else neighbour.before(element);
          continue;
        }
      }
      const z = Math.max(0, zIndexOf(element, view) + direction);
      setInline(element, { 'z-index': String(z), ...(position === 'static' ? { position: 'relative' } : {}) });
    }
    commit();
  };

  const selectParent = (): void => {
    const element = selectionRef.current[selectionRef.current.length - 1];
    if (editingRef.current) finishText();
    const parent = element ? parentTarget(element, backdrop()) : null;
    if (parent) select([parent]);
  };

  const align = (mode: AlignMode): void => {
    const elements = selectionRef.current;
    if (elements.length === 0) return;
    if (editingRef.current) finishText();
    const boxes = elements.map(boxOf);
    const document_ = doc();
    const parent = elements[0]!.parentElement;
    const container =
      elements.length > 1
        ? unionBox(boxes)!
        : parent && parent !== document_?.body && parent !== document_?.documentElement
          ? boxOf(parent)
          : frameBox;
    moveBy(elements, alignDeltas(boxes, mode, container));
    commit();
  };

  const nudge = (dx: number, dy: number): void => {
    for (const element of selectionRef.current) applyMove(element, prepareMove(element), dx, dy);
    bump();
  };

  /** 숫자로 옮기고 크기를 바꾼다 — 끌기와 같은 길. 여럿이면 다 담는 상자를 옮긴다. */
  const applyBox = (next: Partial<Box>, final: boolean): void => {
    const elements = selectionRef.current;
    if (elements.length === 0) return;
    if (elements.length > 1) {
      const union = unionBox(elements.map(boxOf))!;
      const dx = next.x === undefined ? 0 : next.x - union.x;
      const dy = next.y === undefined ? 0 : next.y - union.y;
      moveBy(
        elements,
        elements.map(() => ({ dx, dy })),
      );
    } else {
      const element = elements[0]!;
      const start = boxOf(element);
      if (next.width !== undefined || next.height !== undefined) {
        const prepared = prepareResize(element);
        setInline(
          element,
          sizedStyle({ width: next.width ?? start.width, height: next.height ?? start.height }, prepared.extra),
        );
      }
      if (next.x !== undefined || next.y !== undefined) {
        applyMove(element, prepareMove(element), (next.x ?? start.x) - start.x, (next.y ?? start.y) - start.y);
      }
    }
    if (final) commit();
    else commitSoon();
  };

  /** 입력 칸의 값 — 고른 입력 칸 모두에(`components.ts`). */
  const applyFieldValue = (value: string): void => {
    for (const element of selectionRef.current) if (fieldKind(element)) setFieldValue(element, value);
    // 이 칸에 연결한 D-day 가 바로 다시 계산된다.
    const body = doc()?.body;
    if (body) refreshDdays(body);
    commit();
  };

  /** D-day 설정 — 고른 D-day 모두에. 날짜 칸을 연결하면 그 칸에 이름(id)이 없을 때 붙인다. */
  const applyDday = (patch: Partial<DdaySettings>, link?: HTMLElement | null): void => {
    const from = link === undefined ? undefined : link ? ensureId(link) : null;
    for (const element of selectionRef.current) {
      if (!isDday(element)) continue;
      writeDday(element, { ...readDday(element), ...patch, ...(from === undefined ? {} : { from }) });
    }
    commit();
  };

  const applyStyle = (changes: Record<string, string | null>, final: boolean): void => {
    for (const element of selectionRef.current) setInline(element, changes);
    if (final) commit();
    else commitSoon();
  };

  /** 바탕이 어두우면 흰 글, 밝으면 검은 글 — 새 글이 처음부터 보이게. */
  const readableColor = (): string => {
    const document_ = doc();
    const view = win();
    if (!document_?.body || !view) return '#111827';
    const color = [frameRoot(), document_.body, document_.documentElement]
      .filter((element): element is HTMLElement => !!element)
      .map((element) => parseCssColor(view.getComputedStyle(element).backgroundColor))
      .find((parsed) => parsed && parsed.a > 0);
    if (!color) return '#111827';
    return 0.299 * color.r + 0.587 * color.g + 0.114 * color.b < 128 ? '#ffffff' : '#111827';
  };

  /** 지금 보이는 캔버스의 가운데 — 화면 안으로 맞춘 문서 좌표. 새 글·그림·상자가 여기에 놓인다. */
  const visibleCenter = (): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: design.width / 2, y: design.height / 2 };
    const x = (rect.width / 2 - pan.x) / zoom;
    const y = (rect.height / 2 - pan.y) / zoom;
    return { x: Math.max(0, Math.min(design.width, x)), y: Math.max(0, Math.min(design.height, y)) };
  };

  /** 넣은 요소를 보이는 가운데로 — 크기를 굳히지 않은 글은 놓고 나서 재야 가운데가 맞는다. */
  const centerOn = (element: HTMLElement, center: Point): void => {
    const box = boxOf(element);
    applyMove(
      element,
      moveOrigin(computed(element)),
      Math.round(center.x - box.width / 2 - box.x),
      Math.round(center.y - box.height / 2 - box.y),
    );
  };

  /** 넣기 메뉴의 요소를 보이는 가운데에 — 글이면 바로 고치기를 시작한다(안내 글이 골린 채라 치면 바뀐다). */
  const insertComponent = (id: ComponentId): void => {
    setInsertOpen(false);
    if (id === 'image') {
      chooseImage('add');
      return;
    }
    if (id === 'video') {
      chooseVideo('add');
      return;
    }
    const document_ = doc();
    const parent = frameRoot();
    if (!document_ || !parent) return;
    if (editingRef.current) finishText();
    const built = buildComponent(id, document_, {
      width: design.width,
      height: design.height,
      ink: readableColor(),
      accent: docColors[0] ?? '#2563eb',
    });
    const { element } = built;
    parent.append(element);
    const step = (insertStepRef.current++ % 6) * 32;
    const base = visibleCenter();
    const center = { x: base.x + step, y: base.y + step };
    placeAbsolute(element, {
      x: center.x - built.width / 2,
      y: center.y - built.height / 2,
      width: built.width,
      height: built.height,
    });
    if (built.grow) {
      // 글은 친 만큼 늘어나게 — 크기를 굳히지 않는다. 놓고 나서 재야 가운데가 맞는다.
      setInline(element, { width: null, height: null, 'max-width': null });
      centerOn(element, center);
    }
    raiseNew(element);
    select([element]);
    commit();
    const view = win();
    if (view && isTextLayer(element, view)) startText(element, true);
  };

  /**
   * 그림 파일을 읽는다 — 긴 변이 2048px 를 넘거나 파일이 크면 줄여서(PNG 는 PNG 로 — 투명한 곳을 지키려고, 나머지는 JPEG)
   * 문서에 넣는다. 움직이는 GIF·SVG 는 그대로. 크기는 콘솔 쪽에서 읽어 둔다(문서 안의 그림은 다 읽혔다고 알려 주지 않는다).
   */
  const readImage = (file: File, then: (source: string, width: number, height: number) => void): void => {
    if (!file.type.startsWith('image/')) {
      setError('그림 파일(PNG·JPEG·GIF·WebP·SVG)을 고르세요');
      return;
    }
    if (file.size > IMAGE_FILE_MAX_BYTES) {
      setError(`그림 파일은 ${IMAGE_FILE_MAX_BYTES / 1024 / 1024}MB 까지 고를 수 있습니다`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const original = reader.result;
      const probe = new Image();
      probe.onerror = () => setError('그림을 읽지 못했습니다');
      probe.onload = () => {
        const width = probe.naturalWidth || 4;
        const height = probe.naturalHeight || 3;
        const keep = /image\/(gif|svg)/.test(file.type);
        const size = fitWithin(width, height, IMAGE_MAX_SIDE);
        let source = original;
        if (!keep && (size.width < width || file.size > 1024 * 1024)) {
          const canvas = document.createElement('canvas');
          canvas.width = size.width;
          canvas.height = size.height;
          canvas.getContext('2d')?.drawImage(probe, 0, 0, size.width, size.height);
          source =
            file.type === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.86);
        }
        // data: 글자는 원래 크기의 4/3 쯤이다.
        if (source.length * 0.75 > IMAGE_MAX_BYTES) {
          setError(
            `그림이 너무 큽니다 — 줄여도 ${IMAGE_MAX_BYTES / 1024 / 1024}MB 를 넘습니다(문서 안에 함께 저장되기 때문입니다)`,
          );
          return;
        }
        then(source, size.width, size.height);
      };
      probe.src = original;
    };
    reader.readAsDataURL(file);
  };

  const addImage = (file: File): void => {
    readImage(file, (source, naturalWidth, naturalHeight) => {
      const document_ = doc();
      const parent = frameRoot();
      if (!document_ || !parent) return;
      const width = Math.round(Math.min(naturalWidth, design.width * 0.3));
      const height = Math.round((width * naturalHeight) / naturalWidth);
      const center = visibleCenter();
      const element = document_.createElement('img');
      element.alt = '';
      element.src = source;
      parent.append(element);
      placeAbsolute(element, { x: center.x - width / 2, y: center.y - height / 2, width, height });
      raiseNew(element);
      select([element]);
      commit();
    });
  };

  /** 고른 그림을 다른 그림으로 — 자리와 크기는 그대로 두고, 새 그림은 그 안에 비율대로(object-fit). */
  const replaceImage = (file: File): void => {
    const element = selectionRef.current[0];
    if (!element || element.tagName.toLowerCase() !== 'img') return;
    readImage(file, (source) => {
      const box = boxOf(element);
      const prepared = prepareResize(element);
      setInline(element, { ...sizedStyle(box, prepared.extra), 'object-fit': 'contain' });
      element.removeAttribute('srcset');
      element.setAttribute('src', source);
      commit();
    });
  };

  /** 고른 그림 영역에 그림을 넣는다 — 처음이면 점선을 걷고, 이미 있으면 바꾼다. */
  const fillFrame = (file: File): void => {
    const frame = selectionRef.current[0];
    if (!frame || !isImageFrame(frame)) return;
    readImage(file, (source) => {
      fillImageFrame(frame, source);
      select([frame]);
      commit();
    });
  };

  const chooseImage = (mode: 'add' | 'replace' | 'frame'): void => {
    if (editingRef.current) finishText();
    fileModeRef.current = mode;
    fileRef.current?.click();
  };

  /**
   * 영상 파일을 콘텐츠로 올리고(서버가 화면이 푸는 모양으로 바꾼다) 그 자산 id 를 돌려준다. 크기는 콘솔 쪽에서 내 컴퓨터의
   * 파일로 읽는다 — 올린 영상은 변환이 끝나기 전엔 주소가 없을 수 있다. 편집 화면에는 내 컴퓨터의 파일을 보인다.
   */
  const readVideo = async (file: File): Promise<{ id: string; width: number; height: number } | null> => {
    if (!uploadVideo) return null;
    if (!file.type.startsWith('video/')) {
      setError('영상 파일(MP4·MOV·WebM 등)을 고르세요');
      return null;
    }
    const local = URL.createObjectURL(file);
    const size = await new Promise<{ width: number; height: number }>((resolve) => {
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.muted = true;
      probe.onloadedmetadata = () => resolve({ width: probe.videoWidth || 1920, height: probe.videoHeight || 1080 });
      // 브라우저가 못 푸는 영상도 서버는 바꿔 틀 수 있다 — 크기만 16:9 로 짐작한다.
      probe.onerror = () => resolve({ width: 1920, height: 1080 });
      probe.src = local;
    });
    setVideoBusy(true);
    setError(null);
    try {
      const { id } = await uploadVideo(file);
      videoSourcesRef.current.set(id, local);
      return { id, ...size };
    } catch (cause) {
      URL.revokeObjectURL(local);
      setError(cause instanceof Error ? `영상을 올리지 못했습니다 — ${cause.message}` : '영상을 올리지 못했습니다');
      return null;
    } finally {
      setVideoBusy(false);
    }
  };

  const addVideo = async (file: File): Promise<void> => {
    const read = await readVideo(file);
    const document_ = doc();
    const parent = frameRoot();
    if (!read || !document_ || !parent) return;
    const width = Math.round(design.width * 0.4);
    const height = Math.round((width * read.height) / read.width);
    const center = visibleCenter();
    const element = createVideo(document_, read.id);
    parent.append(element);
    placeAbsolute(element, { x: center.x - width / 2, y: center.y - height / 2, width, height });
    showVideos();
    raiseNew(element);
    select([element]);
    commit();
  };

  /** 고른 영상을 다른 영상으로 — 자리·크기·맞춤·소리는 그대로. */
  const replaceVideo = async (file: File): Promise<void> => {
    const element = selectionRef.current[0];
    if (!element || !isVideo(element)) return;
    const read = await readVideo(file);
    if (!read || !element.isConnected) return;
    setVideoAsset(element as HTMLVideoElement, read.id);
    showVideos();
    commit();
  };

  const chooseVideo = (mode: 'add' | 'replace'): void => {
    if (editingRef.current) finishText();
    videoModeRef.current = mode;
    videoFileRef.current?.click();
  };

  // ── 확대·이동

  const setZoom = (next: number): void => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const center = { x: rect.width / 2, y: rect.height / 2 };
    setView((current) => {
      const zoomed = clampZoom(next);
      return { zoom: zoomed, pan: zoomAround(current.zoom, current.pan, center, zoomed), fit: false };
    });
  };
  const fitFrame = (): void => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) setView({ ...fitView(rect, frameBox, 40), fit: true });
  };

  // 휠 — 화면을 옮기고, Ctrl·⌘(또는 두 손가락 오므리기)면 확대. 기본 동작(브라우저 확대·굴리기)을 막아야 해서 직접 단다.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const at = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      setView((current) => {
        if (event.ctrlKey || event.metaKey) {
          const delta = Math.max(-50, Math.min(50, event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY));
          const next = clampZoom(current.zoom * Math.exp(-delta * 0.01));
          return { zoom: next, pan: zoomAround(current.zoom, current.pan, at, next), fit: false };
        }
        const dx = event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX;
        const dy = event.shiftKey && !event.deltaX ? 0 : event.deltaY;
        return { ...current, pan: { x: current.pan.x - dx, y: current.pan.y - dy }, fit: false };
      });
    };
    const stopGesture = (event: Event): void => event.preventDefault();
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('gesturestart', stopGesture);
    canvas.addEventListener('gesturechange', stopGesture);
    return () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('gesturestart', stopGesture);
      canvas.removeEventListener('gesturechange', stopGesture);
    };
  }, []);

  // ── 캔버스의 마우스

  const focusDialog = (): void => {
    // 오른쪽 칸의 숫자 칸에 있던 초점을 거둔다 — 그 칸이 값을 반영하고, 글쇠가 다시 편집으로 온다.
    if (document.activeElement !== dialogRef.current) dialogRef.current?.focus({ preventScroll: true });
  };

  const startMove = (point: Point, click: Extract<Drag, { kind: 'move' }>['click']): void => {
    dragRef.current = { kind: 'move', start: point, moved: false, items: [], base: null, targets: [], click };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    setMenu(null);
    if (event.button === 2) return;
    const canvas = canvasRef.current!;
    canvas.setPointerCapture(event.pointerId);
    if (event.button === 1 || spaceRef.current) {
      event.preventDefault();
      dragRef.current = { kind: 'pan', x: event.clientX, y: event.clientY, pan: view.pan };
      setPanning(true);
      return;
    }
    if (event.button !== 0) return;
    const point = pointOf(event);
    const editing = editingRef.current;
    if (editing) {
      // 고치는 글 안이면 커서를 그 자리에 둔다. 밖이면 고치기를 끝내고 그 자리의 것을 고른다.
      const range = inside(boxOf(editing), point) ? caretAt(point) : null;
      if (range && editing.contains(range.startContainer)) {
        editing.focus({ preventScroll: true });
        setCaret(range, range);
        dragRef.current = { kind: 'text', anchor: range };
        return;
      }
      finishText();
    }
    focusDialog();

    const selection = selectionRef.current;
    const handle = (event.target as HTMLElement).dataset?.handle as Handle | undefined;
    if (handle && selection.length === 1) {
      const element = selection[0]!;
      const prepared = prepareResize(element);
      dragRef.current = {
        kind: 'resize',
        element,
        handle,
        start: point,
        box: boxOf(element),
        origin: prepared.origin,
        extra: prepared.extra,
        // 그림은 모서리로 끌면 비율을 지킨다(Shift 를 누르면 반대).
        keepRatio: (element.tagName.toLowerCase() === 'img' || element.namespaceURI === SVG_NS) !== event.shiftKey,
        moved: false,
        targets: snapTargets([element]),
      };
      return;
    }

    const target = pickAt(point);
    const union = unionBox(selection.map(boxOf));
    if (union && inside(union, point) && !event.shiftKey) {
      // 고른 것 위에서 누르면 끌어 옮긴다. 끌지 않고 떼면 — 고른 글이면 고치기, 그 안의 다른 것이면 그것을 고른다.
      const only = selection.length === 1 ? selection[0]! : null;
      const view = win();
      const click =
        only && target === only && view && isTextLayer(only, view)
          ? { edit: only }
          : target && !selection.includes(target) && selection.some((element) => element.contains(target))
            ? { pick: target }
            : null;
      startMove(point, click);
      return;
    }
    if (!target) {
      // 빈 곳 — 고른 것을 풀고, 끌면 화면을 옮긴다.
      if (!event.shiftKey) select([]);
      dragRef.current = { kind: 'pan', x: event.clientX, y: event.clientY, pan: view.pan };
      return;
    }
    if (event.shiftKey) {
      const next = selection.includes(target)
        ? selection.filter((element) => element !== target)
        : [...selection, target];
      select(next);
      if (!next.includes(target)) return;
    } else select([target]);
    insertStepRef.current = 0;
    startMove(point, null);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const point = pointOf(event);
    const drag = dragRef.current;
    if (!drag) {
      const target = editingRef.current || spaceRef.current ? null : pickAt(point);
      setHovered(target && !selectionRef.current.includes(target) ? target : null);
      return;
    }
    if (drag.kind === 'pan') {
      if (!panning && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) > 3) setPanning(true);
      setView((current) => ({
        ...current,
        fit: false,
        pan: { x: drag.pan.x + event.clientX - drag.x, y: drag.pan.y + event.clientY - drag.y },
      }));
      return;
    }
    if (drag.kind === 'text') {
      const range = caretAt(point);
      if (range && editingRef.current?.contains(range.startContainer)) setCaret(drag.anchor, range);
      return;
    }
    const dx = point.x - drag.start.x;
    const dy = point.y - drag.start.y;
    if (!drag.moved && Math.hypot(dx, dy) * zoom < 4) return;
    const free = event.ctrlKey || event.metaKey;
    if (drag.kind === 'move') {
      if (!drag.moved) {
        drag.moved = true;
        drag.items = selectionRef.current.map((element) => ({ element, origin: prepareMove(element) }));
        drag.base = unionBox(drag.items.map((item) => boxOf(item.element)));
        drag.targets = snapTargets(selectionRef.current);
      }
      let moveX = dx;
      let moveY = dy;
      let lines: Guide[] = [];
      if (drag.base && !free) {
        const snapped = snapMove(
          { ...drag.base, x: drag.base.x + dx, y: drag.base.y + dy },
          drag.targets,
          SNAP_PX / zoom,
        );
        moveX += snapped.dx;
        moveY += snapped.dy;
        lines = snapped.guides;
      }
      for (const item of drag.items) applyMove(item.element, item.origin, moveX, moveY);
      setGuides(lines);
      setHovered(null);
      bump();
      return;
    }
    if (!drag.moved) setResizing(true);
    drag.moved = true;
    let box = resizeBox(drag.box, drag.handle, dx, dy, drag.keepRatio);
    let lines: Guide[] = [];
    if (!drag.keepRatio && !free) {
      const snapped = snapResize(box, drag.handle, drag.targets, SNAP_PX / zoom);
      box = snapped.box;
      lines = snapped.guides;
    }
    applyResize(drag, box);
    setGuides(lines);
    bump();
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    setGuides([]);
    setPanning(false);
    setResizing(false);
    if (!drag) return;
    if (drag.kind === 'move') {
      if (drag.moved) commit();
      else if (drag.click && 'edit' in drag.click) startTextAt(drag.click.edit, pointOf(event));
      else if (drag.click) select([drag.click.pick]);
    }
    if (drag.kind === 'resize' && drag.moved) commit();
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const point = pointOf(event);
    if (editingRef.current) {
      selectWordAt(point);
      return;
    }
    const target = pickAt(point);
    const view = win();
    if (target && isImageFrame(target)) {
      select([target]);
      chooseImage('frame');
    } else if (target && target.tagName.toLowerCase() === 'img') {
      select([target]);
      chooseImage('replace');
    } else if (target && view && isTextLayer(target, view)) {
      startTextAt(target, point);
      selectWordAt(point);
    }
  };

  const onContextMenu = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (editingRef.current) finishText();
    const target = pickAt(pointOf(event));
    if (!target) {
      setMenu(null);
      return;
    }
    if (!selectionRef.current.some((element) => element.contains(target))) select([target]);
    setMenu({ x: event.clientX, y: event.clientY });
  };

  // ── 글쇠 — 꼭 필요한 것만. 한글 입력 상태에서도 듣게 글자가 아니라 글쇠 자리(`code`)로 본다.
  const history = historyRef.current;
  const dirty = !!history?.dirty || design.width !== initialDesign.width || design.height !== initialDesign.height;
  const close = useGuardedClose(dirty, onCancel);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.isComposing) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      )
        return;
      if (editingRef.current) return;
      const mod = event.metaKey || event.ctrlKey;
      const code = event.code;
      const has = selectionRef.current.length > 0;
      const run = (action: () => void): void => {
        event.preventDefault();
        action();
      };
      if (code === 'Space') {
        event.preventDefault();
        if (!spaceRef.current) {
          spaceRef.current = true;
          setSpaceHeld(true);
        }
        return;
      }
      if (mod) {
        if (code === 'KeyZ') return run(event.shiftKey ? redo : undo);
        if (code === 'KeyY') return run(redo);
        if (code === 'KeyC' && has) return run(copySelection);
        if (code === 'KeyX' && has) return run(() => (copySelection(), removeSelection()));
        if (code === 'KeyV') return run(paste);
        if (code === 'KeyD' && has) return run(duplicateSelection);
        if (code === 'Equal' || code === 'NumpadAdd') return run(() => setZoom(stepZoom(viewRef.current.zoom, 1)));
        if (code === 'Minus' || code === 'NumpadSubtract')
          return run(() => setZoom(stepZoom(viewRef.current.zoom, -1)));
        if (code === 'Digit0') return run(fitFrame);
        return;
      }
      if (code === 'Escape') {
        event.preventDefault();
        if (menu) setMenu(null);
        else if (has) select([]);
        else close();
        return;
      }
      if (code === 'Enter' && selectionRef.current.length === 1) {
        const view = win();
        const element = selectionRef.current[0]!;
        if (view && isTextLayer(element, view)) run(() => startText(element));
        return;
      }
      if (code === 'Delete' || code === 'Backspace') {
        if (has) run(removeSelection);
        return;
      }
      if (!has) return;
      const step = event.shiftKey ? 10 : 1;
      const arrows: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      if (arrows[code]) run(() => nudge(...arrows[code]!));
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.code === 'Space') {
        spaceRef.current = false;
        setSpaceHeld(false);
      }
      // 방향키는 뗄 때 한 번 남긴다 — 누르고 있는 동안 칸마다 되돌리기가 쌓이지 않게.
      if (event.code.startsWith('Arrow') && selectionRef.current.length > 0 && !editingRef.current) commit();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  });

  // 열 때 초점을 창 안으로 — 뒤쪽 화면의 단추에 초점 테두리가 남아 비치지 않게. 닫으면 그 단추로 돌려준다.
  const [returnTo] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null));
  useEffect(() => {
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
    };
  }, [returnTo]);

  const save = async (): Promise<void> => {
    if (editingRef.current) finishText();
    flush();
    setSaving(true);
    setError(null);
    try {
      await onSave(serialize(), design);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  // ── 오른쪽 칸에 넘길 것

  const fonts = useMemo(() => {
    const names = new Set<string>();
    doc()?.fonts?.forEach((face) => names.add(face.family.replace(/^["']|["']$/g, '')));
    return [...names];
    // 문서가 바뀔 때만 다시 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure, ready]);

  // 이 화면이 쓰는 색 — 글자 색·바탕 색을 많이 쓴 차례로(문서가 크면 앞의 2,000개만 본다).
  const docColors = useMemo(() => {
    const view = win();
    const body = doc()?.body;
    if (!view || !body) return [];
    const values: string[] = [];
    for (const element of [body, ...Array.from(body.querySelectorAll('*')).slice(0, 2000)]) {
      const style = view.getComputedStyle(element);
      values.push(style.color, style.backgroundColor);
    }
    return topColors(values);
    // 문서를 처음 열 때만 — 고칠 때마다 견본이 바뀌면 누르려던 자리가 달라진다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const selection = selectionRef.current.filter((element) => element.isConnected);
  const primary = selection[selection.length - 1] ?? null;
  const view_ = win();
  const primaryIsText = !!primary && !!view_ && selection.length === 1 && isTextLayer(primary, view_);
  const primaryIsImage = !!primary && selection.length === 1 && primary.tagName.toLowerCase() === 'img';
  const primaryIsFrame = !!primary && selection.length === 1 && isImageFrame(primary);
  const primaryIsVideo = !!primary && selection.length === 1 && isVideo(primary);
  const canSelectParent = !!primary && !!ready && parentTarget(primary, backdrop()) !== null;

  const editActions: EditActions = {
    style: applyStyle,
    box: applyBox,
    align,
    reorder,
    textEdit: () => {
      if (primary && primaryIsText) startText(primary);
    },
    textDone: finishText,
    replaceImage: () => chooseImage('replace'),
    replaceVideo: () => chooseVideo('replace'),
    videoFit: (fit: ImageFit) => {
      for (const element of selectionRef.current) if (isVideo(element)) setVideoFit(element, fit);
      commit();
    },
    videoSound: (on: boolean) => {
      for (const element of selectionRef.current) if (isVideo(element)) setVideoSound(element as HTMLVideoElement, on);
      commit();
    },
    frameImage: () => chooseImage('frame'),
    frameFit: (fit: ImageFit) => {
      for (const element of selectionRef.current) if (isImageFrame(element)) setFrameFit(element, fit);
      commit();
    },
    frameClear: () => {
      for (const element of selectionRef.current) if (isImageFrame(element)) clearImageFrame(element);
      commit();
    },
    fieldValue: applyFieldValue,
    dday: (patch) => applyDday(patch),
    ddayLink: (field) => applyDday({}, field),
    selectOptions: (lines) => {
      for (const element of selectionRef.current)
        if (element.tagName.toLowerCase() === 'select') setSelectOptions(element as HTMLSelectElement, lines);
      commit();
    },
    placeholder: (text) => {
      for (const element of selectionRef.current) {
        if (text) element.setAttribute('placeholder', text);
        else element.removeAttribute('placeholder');
      }
      commit();
    },
    selectParent,
    duplicate: duplicateSelection,
    remove: removeSelection,
    reset: resetSelection,
    resetAll,
    design: (size) => {
      setDesign(size);
      select([]);
    },
    pageBackground: (css, final) => {
      const body = doc()?.body;
      if (!body) return;
      setInline(body, { background: css, 'background-color': null, 'background-image': null });
      if (final) commit();
      else commitSoon();
    },
  };

  // ── 캔버스에 그릴 것(화면 좌표)

  const selectionBoxes = view_ ? selection.map((element) => toScreen(boxOf(element))) : [];
  const union = unionBox(selectionBoxes);
  const docUnion = view_ && selection.length > 0 ? unionBox(selection.map(boxOf)) : null;
  const single = selection.length === 1 && !editingText;
  const hoverBox = hovered && hovered.isConnected && view_ ? toScreen(boxOf(hovered)) : null;
  const hoverLabel =
    hovered && view_
      ? isImageFrame(hovered)
        ? '그림 영역'
        : isDday(hovered)
          ? 'D-day'
          : isTextLayer(hovered, view_)
          ? '글'
          : elementLabel(hovered.tagName, false, layerChildren(hovered).length)
      : '';
  const frameScreen = toScreen(frameBox);
  const cursor = panning ? 'grabbing' : spaceHeld ? 'grab' : editingText ? 'text' : hovered ? 'pointer' : 'default';
  const empty = !!view_ && ready && layerChildren(frameRoot() ?? doc()!.body).length === 0;
  const hint = videoBusy
    ? '영상을 올리는 중입니다 — 큰 영상은 몇 분 걸립니다. 다 올라가면 화면에 들어옵니다.'
    : editingText
    ? '글을 고치는 중입니다 — 다 쓰면 바깥을 누르거나 [다 고쳤습니다] 를 누르세요.'
    : primaryIsText
      ? '끌면 옮겨지고, 동그라미를 끌면 크기가 바뀝니다. 한 번 더 누르면 글을 고칩니다.'
      : selection.length > 0
        ? '끌면 옮겨지고, 동그라미를 끌면 크기가 바뀝니다. 색과 모양은 오른쪽에서 바꿉니다.'
        : empty
          ? '빈 화면입니다 — 위의 [넣기] 로 제목·글·그림을 넣어 시작하세요.'
          : '고칠 글·그림·상자를 누르세요. 빈 곳을 끌면 화면이 옮겨집니다.';
  const menuItems: MenuItem[] = [
    ...(primaryIsText ? [{ label: '글 고치기', run: () => primary && startText(primary) }] : []),
    ...(primaryIsImage ? [{ label: '다른 그림으로 바꾸기', run: () => chooseImage('replace') }] : []),
    ...(primaryIsVideo ? [{ label: '다른 영상으로 바꾸기', run: () => chooseVideo('replace') }] : []),
    ...(primaryIsFrame
      ? [{ label: frameImage(primary) ? '다른 그림으로 바꾸기' : '그림 고르기', run: () => chooseImage('frame') }]
      : []),
    { label: '복제', run: duplicateSelection },
    { label: '앞으로', run: () => reorder(1) },
    { label: '뒤로', run: () => reorder(-1) },
    { label: '감싼 상자 고르기', disabled: !canSelectParent, run: selectParent },
    { label: '원래대로', disabled: !canReset, run: resetSelection },
    { label: '지우기', danger: true, run: removeSelection },
  ];

  // 고른 것 바로 위의 단추 — 위에 자리가 없으면 아래에. 캔버스 밖으로 나가지 않게 가운데를 당긴다.
  const actionBar = union && !resizing && !dragRef.current
    ? {
        left: Math.max(130, Math.min(canvasSize.width - 130, union.x + union.width / 2)),
        top: union.y - 50 >= 8 ? union.y - 50 : Math.min(canvasSize.height - 52, union.y + union.height + 12),
      }
    : null;

  return createPortal(
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex flex-col bg-slate-200 text-slate-900 outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={`화면 편집 — ${title}`}
      tabIndex={-1}
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-4">
        <div className="min-w-0 max-w-[260px]">
          <div className="truncate text-[14px] font-semibold" title={title}>
            {title}
          </div>
          <div className="text-[11.5px] text-slate-500">화면 편집</div>
        </div>
        <span className="mx-2 h-7 w-px bg-slate-200" />
        <div className="relative">
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={insertOpen}
            disabled={!ready}
            onClick={() => setInsertOpen((open) => !open)}
            className={`flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold disabled:opacity-40 ${
              insertOpen ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
          >
            <Plus className="h-4 w-4" />
            넣기
          </button>
          {insertOpen ? (
            <InsertMenu
              onPick={insertComponent}
              onClose={() => setInsertOpen(false)}
              hidden={uploadVideo ? [] : ['video']}
            />
          ) : null}
        </div>
        <span className="mx-2 h-7 w-px bg-slate-200" />
        <BarButton
          icon={<Undo2 className="h-4 w-4" />}
          label="되돌리기"
          title={`되돌리기 (${MOD}+Z)`}
          onClick={undo}
          disabled={!history?.canUndo}
        />
        <BarButton
          icon={<Redo2 className="h-4 w-4" />}
          label="다시 하기"
          title={`다시 하기 (${MOD}+Shift+Z)`}
          onClick={redo}
          disabled={!history?.canRedo}
        />
        <span className="flex-1" />
        {saveExtras ? <div className="text-[12.5px]">{saveExtras}</div> : null}
        <button
          type="button"
          className="h-9 rounded-lg px-4 text-[13px] font-medium text-slate-700 hover:bg-slate-100"
          onClick={close}
          disabled={saving}
        >
          닫기
        </button>
        <button
          type="button"
          className="h-9 rounded-lg bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
          onClick={() => void save()}
          disabled={!ready || saving || (!dirty && !savePristine)}
        >
          {saving ? '저장 중…' : saveLabel}
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        <div
          ref={canvasRef}
          className="relative min-w-0 flex-1 touch-none select-none overflow-hidden"
          style={{ cursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={() => setHovered(null)}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          // 캔버스를 눌러도 초점이 옮겨 가지 않게 — 고치던 글에서 초점이 빠지면 치는 글자가 사라진다.
          onMouseDown={(event) => event.preventDefault()}
        >
          <div
            className="pointer-events-none absolute overflow-hidden bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_4px_24px_rgba(0,0,0,0.14)]"
            style={{ left: frameScreen.x, top: frameScreen.y, width: frameScreen.width, height: frameScreen.height }}
          >
            <div
              style={{ width: design.width, height: design.height, transform: `scale(${zoom})`, transformOrigin: '0 0' }}
            >
              <iframe
                ref={frameRef}
                title={`편집 — ${title}`}
                sandbox="allow-same-origin"
                scrolling="no"
                className="block border-0 bg-white"
                style={{ width: design.width, height: design.height }}
              />
            </div>
          </div>

          <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible">
            {guides.map((guide, index) =>
              guide.axis === 'x' ? (
                <line
                  key={index}
                  x1={guide.at * zoom + pan.x}
                  x2={guide.at * zoom + pan.x}
                  y1={guide.from * zoom + pan.y}
                  y2={guide.to * zoom + pan.y}
                  stroke={RED}
                  strokeWidth={1}
                />
              ) : (
                <line
                  key={index}
                  y1={guide.at * zoom + pan.y}
                  y2={guide.at * zoom + pan.y}
                  x1={guide.from * zoom + pan.x}
                  x2={guide.to * zoom + pan.x}
                  stroke={RED}
                  strokeWidth={1}
                />
              ),
            )}
          </svg>

          {hoverBox ? (
            <>
              <Outline box={hoverBox} color={BLUE} width={1.5} dashed />
              <span
                className="pointer-events-none absolute rounded px-1.5 text-[11px] font-medium leading-[18px] text-white"
                style={{ left: hoverBox.x, top: Math.max(0, hoverBox.y - 20), background: BLUE }}
              >
                {hoverLabel}
              </span>
            </>
          ) : null}
          {selection.length > 1
            ? selectionBoxes.map((box, index) => <Outline key={index} box={box} color={BLUE} width={1.5} />)
            : null}
          {union ? <Outline box={union} color={BLUE} width={2} /> : null}
          {single && union
            ? HANDLES.map((handle) => {
                const point = handlePoint(union, handle);
                return (
                  <span
                    key={handle}
                    data-handle={handle}
                    className="absolute h-3 w-3 rounded-full border-2 bg-white shadow-sm"
                    style={{ left: point.x - 6, top: point.y - 6, borderColor: BLUE, cursor: HANDLE_CURSOR[handle] }}
                  />
                );
              })
            : null}
          {resizing && union && docUnion ? (
            <span
              className="pointer-events-none absolute -translate-x-1/2 whitespace-nowrap rounded px-1.5 text-[11px] font-medium leading-[18px] text-white"
              style={{ left: union.x + union.width / 2, top: union.y + union.height + 10, background: BLUE }}
            >
              {Math.round(docUnion.width)} × {Math.round(docUnion.height)}
            </span>
          ) : null}

          {actionBar ? (
            <div
              className="absolute flex -translate-x-1/2 items-center gap-0.5 rounded-xl bg-white p-1 shadow-[0_4px_16px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)]"
              style={actionBar}
              onPointerDown={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onContextMenu={(event) => event.stopPropagation()}
            >
              {editingText ? (
                <FloatButton icon={<Check className="h-4 w-4" />} label="다 고쳤습니다" tone="primary" onClick={finishText} />
              ) : (
                <>
                  {primaryIsText ? (
                    <FloatButton
                      icon={<Pencil className="h-4 w-4" />}
                      label="글 고치기"
                      onClick={() => primary && startText(primary)}
                    />
                  ) : null}
                  {primaryIsImage ? (
                    <FloatButton
                      icon={<ImageUp className="h-4 w-4" />}
                      label="그림 바꾸기"
                      onClick={() => chooseImage('replace')}
                    />
                  ) : null}
                  {primaryIsFrame ? (
                    <FloatButton
                      icon={<ImageUp className="h-4 w-4" />}
                      label={frameImage(primary) ? '그림 바꾸기' : '그림 고르기'}
                      tone={frameImage(primary) ? 'plain' : 'primary'}
                      onClick={() => chooseImage('frame')}
                    />
                  ) : null}
                  {primaryIsVideo && uploadVideo ? (
                    <FloatButton
                      icon={<Clapperboard className="h-4 w-4" />}
                      label="영상 바꾸기"
                      onClick={() => chooseVideo('replace')}
                    />
                  ) : null}
                  <FloatButton icon={<Copy className="h-4 w-4" />} label="복제" onClick={duplicateSelection} />
                  <FloatButton icon={<Trash2 className="h-4 w-4" />} label="지우기" tone="danger" onClick={removeSelection} />
                </>
              )}
            </div>
          ) : null}

          {(note && showNote) || error ? (
            <div
              className="absolute left-1/2 top-3 flex max-w-[min(680px,calc(100%-32px))] -translate-x-1/2 items-start gap-2 rounded-xl bg-white px-3.5 py-2.5 text-[12.5px] leading-relaxed shadow-[0_2px_12px_rgba(0,0,0,0.14)]"
              onPointerDown={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
            >
              <div className="min-w-0 flex-1">
                {error ? <p className="font-medium text-red-600">{error}</p> : null}
                {note && showNote ? <div className="text-slate-600">{note}</div> : null}
              </div>
              <button
                type="button"
                aria-label="알림 닫기"
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-slate-400 hover:bg-slate-100"
                onClick={() => {
                  setError(null);
                  setShowNote(false);
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : null}

          <div
            className="absolute bottom-3 left-3 flex items-center gap-0.5 rounded-xl bg-white p-1 shadow-[0_2px_10px_rgba(0,0,0,0.12)]"
            onPointerDown={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <IconButton label="축소" onClick={() => setZoom(stepZoom(zoom, -1))} icon={<Minus className="h-4 w-4" />} />
            <span className="w-12 text-center text-[12px] font-medium tabular-nums text-slate-600">
              {Math.round(zoom * 100)}%
            </span>
            <IconButton label="확대" onClick={() => setZoom(stepZoom(zoom, 1))} icon={<Plus className="h-4 w-4" />} />
            <button
              type="button"
              className="ml-0.5 flex h-8 items-center gap-1 rounded-lg px-2 text-[12px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
              disabled={view.fit}
              onClick={fitFrame}
            >
              <Maximize className="h-3.5 w-3.5" />
              화면에 맞추기
            </button>
          </div>
          <div className="pointer-events-none absolute bottom-4 left-1/2 max-w-[calc(100%-420px)] -translate-x-1/2 truncate rounded-full bg-slate-900/75 px-4 py-1.5 text-[12.5px] text-white">
            {hint}
          </div>
        </div>

        <aside className="flex w-[300px] shrink-0 flex-col border-l border-slate-200 bg-white">
          {ready ? (
            <EditPanel
              elements={selection}
              getView={getView}
              body={doc()?.body ?? null}
              design={design}
              sizeChoices={sizeChoices}
              editingText={editingText}
              canReset={canReset}
              canResetAll={!!history?.dirty}
              canSelectParent={canSelectParent}
              fonts={fonts}
              docColors={docColors}
              actions={editActions}
            />
          ) : null}
        </aside>
      </div>

      {menu ? (
        <Menu style={menuPosition(menu)} onClose={() => setMenu(null)} items={menuItems} />
      ) : null}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          if (fileModeRef.current === 'replace') replaceImage(file);
          else if (fileModeRef.current === 'frame') fillFrame(file);
          else addImage(file);
        }}
      />
      <input
        ref={videoFileRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          void (videoModeRef.current === 'replace' ? replaceVideo(file) : addVideo(file));
        }}
      />
    </div>,
    document.body,
  );
}

const rectStyle = (box: Box): React.CSSProperties => ({
  left: box.x,
  top: box.y,
  width: box.width,
  height: box.height,
});

function Outline({ box, color, width, dashed = false }: { box: Box; color: string; width: number; dashed?: boolean }): ReactNode {
  return (
    <div
      className="pointer-events-none absolute"
      style={
        dashed
          ? { ...rectStyle(box), outline: `${width}px dashed ${color}`, outlineOffset: -width }
          : { ...rectStyle(box), boxShadow: `inset 0 0 0 ${width}px ${color}` }
      }
    />
  );
}

/** 위 막대의 단추 — 그림과 글자를 함께. */
function BarButton({
  icon,
  label,
  title,
  onClick,
  disabled = false,
}: {
  icon: ReactNode;
  label: string;
  title?: string;
  onClick: () => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-35 disabled:hover:bg-transparent"
    >
      {icon}
      {label}
    </button>
  );
}

/** 고른 것 위에 뜨는 단추. */
function FloatButton({
  icon,
  label,
  onClick,
  tone = 'plain',
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  tone?: 'plain' | 'primary' | 'danger';
}): ReactNode {
  const tones = {
    plain: 'text-slate-700 hover:bg-slate-100',
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    danger: 'text-red-600 hover:bg-red-50',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium ${tones[tone]}`}
    >
      {icon}
      {label}
    </button>
  );
}

function IconButton({ label, icon, onClick }: { label: string; icon: ReactNode; onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="grid h-8 w-8 place-items-center rounded-lg text-slate-700 hover:bg-slate-100"
    >
      {icon}
    </button>
  );
}

interface MenuItem {
  label: string;
  disabled?: boolean;
  danger?: boolean;
  run: () => void;
}

/** 화면 밖으로 나가지 않게 — 메뉴 높이(대략)를 보고 위로 올린다. */
function menuPosition(point: Point): React.CSSProperties {
  const width = 200;
  const height = 300;
  return {
    left: Math.min(point.x, window.innerWidth - width - 8),
    top: Math.max(8, Math.min(point.y, window.innerHeight - height - 8)),
  };
}

function Menu({ items, onClose, style }: { items: MenuItem[]; onClose: () => void; style: React.CSSProperties }): ReactNode {
  return (
    <>
      <div
        className="fixed inset-0 z-[60]"
        onPointerDown={onClose}
        onContextMenu={(event) => (event.preventDefault(), onClose())}
      />
      <div
        role="menu"
        className="fixed z-[61] min-w-[180px] rounded-xl bg-white py-1.5 text-[13px] text-slate-800 shadow-[0_8px_28px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)]"
        style={style}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            className={`flex h-8 w-full items-center px-3.5 text-left hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent ${item.danger ? 'text-red-600' : ''}`}
            onClick={() => {
              onClose();
              item.run();
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
}
